// TabJoust.js — stable rounds, pinned leader, fast countdown, verbose logging
// NOTE: Frame tables are NOT defined here; keep/import:
//   COMMON_FRAMES(), BRANCH_RIGHT_WINS(), BRANCH_LEFT_WINS()

export default class TabJoust {
  constructor(opts = {}) {
    /* ---- Transport / cluster ---- */
    this.CHANNEL = opts.channel || 'tab-joust';
    this.HEARTBEAT_MS = opts.heartbeatMs ?? 1000; // presence ping
    this.STALE_MS = opts.staleMs ?? 3000;         // presence expiry

    /* ---- UI ---- */
    this.debugTabIndex = opts.debugTabIndex ?? true;
    this.titleIcon = opts.titleIcon ?? '⚔️';
    this.IDLE_TITLE = opts.idleTitle || 'Open 3 tabs of this page to start the joust';

    /* ---- Timing ---- */
    this.FRAME_PERIOD_MS = opts.framePeriodMs ?? 320;      // ~13s for a full joust
    this.START_DELAY_MS  = opts.startDelayMs  ?? 2000;     // countdown before the charge
    this.TAIL_HOLD_FRAMES = opts.tailHoldFrames ?? 9;      // hold the result ~3s before the next round

    /* ---- Logging ---- */
    // 0=silent, 1=error, 2=warn, 3=info, 4=debug
    const defaultLevel = (typeof window !== 'undefined' && window.__JOUST_DEBUG__) ? 4 : 3;
    this.logLevel = opts.logLevel ?? defaultLevel;
    this.log = makeLogger('TabJoust', this.logLevel, opts.logger);
    this.log.info('init', { version: 'stable-2', opts: { ...opts, logger: !!opts.logger } });

    /* ---- Frames (must exist in your project) ---- */
    this.SLICE = 13;
    this.common          = COMMON_FRAMES();
    this.branchRightWins = BRANCH_RIGHT_WINS();
    this.branchLeftWins  = BRANCH_LEFT_WINS();

    /* ---- State ---- */
    this.id = crypto.randomUUID?.() || (Math.random().toString(36) + Date.now().toString(36));
    this.joinedAt = Date.now(); // will be replaced by atomic sequence
    this.visible = !document.hidden;

    this.ch = null;
    this._openChannel();

    this.members = new Map(); // id -> { last, visible }
    // Tabs that said 'bye'. A page being left can still be heard for a moment
    // afterwards (e.g. answering the next page's hello on its way into the
    // back/forward cache); without this it counts as a player for a few
    // seconds, and a couple of quick back/forwards made one tab a joust.
    this.gone = new Set();
    this.leaving = false; // this page is being left: stay silent
    this.ORDER_KEY = '__joust_order__';
    this.order = this._loadOrder();
    this._ensureInOrder(); // provisional until we claim sequence

    // 0,1,2 or null
    this.roleIndex = null;

    // Game: { t0 (wall clock), winner, frames: string[][], endIdx, roles }
    this.game = null;

    // Loops
    this.running = false;
    this.hb = null;        // heartbeat interval
    this.hk = null;        // housekeeping tick
    this.paintRaf = 0;     // requestAnimationFrame handle
    this.sendTimer = null; // drift-corrected setTimeout driver

    // Bind
    this._onMsg = this._onMsg.bind(this);
    this._onStorage = this._onStorage.bind(this);
    this._tick = this._tick.bind(this);
    this._drive = this._drive.bind(this);
    this._raf = this._raf.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onPageHide = this._onPageHide.bind(this);
    this._onPageshow = this._onPageshow.bind(this);

    this.favicon = new FaviconHelper({ log: this.log });
    this.hasAppliedWinIcon = false;

    this._originalTitle = document.title;
  }

  /** The tab's own title, shown whenever no joust is on (and restored after). */
  get title() { return this._originalTitle; }

  setTitle(title) {
    this._originalTitle = title;
    if (!this.game) document.title = title;
  }

  /* ================= Lifecycle ================= */

  start() {
    if (this.running) return this;
    this.running = true;

    if (this.ch) this.ch.addEventListener('message', this._onMsg);
    window.addEventListener('storage', this._onStorage);
    document.addEventListener('visibilitychange', this._onVisibility, { passive: true });
    window.addEventListener('pageshow', this._onPageshow, { passive: true });
    // Not beforeunload: it also fires for links that don't leave the page
    // (mailto:, downloads), which would silence a tab that's still here
    window.addEventListener('pagehide', this._onPageHide, { passive: true });

    // Deterministic open order (works with ⌘-open)
    Promise.resolve(this._claimSequence()).then(seq => {
      this.joinedAt = seq;
      this._ensureInOrder();
      this._announce('hello', { id: this.id, visible: this.visible, joinedAt: this.joinedAt });
      this.log.info('hello-sent', this._dbg());
    });

    // Provisional presence in case sequence claim takes a moment
    this._remember(this.id, { visible: this.visible });

    // Heartbeat (presence ping)
    this.hb = setInterval(() => {
      this._remember(this.id, { visible: this.visible });
      this._lastPingAt = Date.now();
      this._announce('ping', { id: this.id, visible: this.visible, joinedAt: this.joinedAt });
      this.log.debug('ping-sent', this._dbg());
    }, this.HEARTBEAT_MS);

    // Housekeeping cadence
    this.hk = setInterval(this._tick, 300);

    // Late-join catch-up (if start/frameIdx already in storage)
    this._ingestLastControlFromStorage();

    // Initial tick and loop setup
    this._tick();
    this._ensureLeaderLoops();

    // Optional periodic diagnostics:
    // this._diagPulse();

    return this;
  }

  stop() {
    if (!this.running) return;
    this.running = false;

    clearInterval(this.hb); clearInterval(this.hk);
    this.hb = this.hk = null;

    this._announce('bye', { id: this.id });
    this.log.info('bye-sent', this._dbg());
    this._removeFromOrder(this.id, /*clearIfEmpty=*/true);

    if (this.ch) { this.ch.removeEventListener('message', this._onMsg); try { this.ch.close?.(); } catch {} this.ch = null; }
    window.removeEventListener('storage', this._onStorage);
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('pageshow', this._onPageshow);
    window.removeEventListener('pagehide', this._onPageHide);

    if (this.sendTimer) { clearTimeout(this.sendTimer); this.sendTimer = null; }
    if (this.paintRaf) { cancelAnimationFrame(this.paintRaf); this.paintRaf = 0; }

    this.members.clear();
    this.roleIndex = null;
    this.game = null;
    document.title = this._originalTitle;
    this.log.info('stopped');
  }

  /* ================= Channel & Announce ================= */

  _openChannel() {
    if ('BroadcastChannel' in self) {
      try {
        this.ch = new BroadcastChannel(this.CHANNEL);
        // probe packet to surface errors in console
        try {
          this.ch.postMessage({ type: '__probe__', from: this.id, ts: Date.now() });
          this.log.info('bc-open', { channel: this.CHANNEL });
        } catch (err) {
          this.log.error('bc-post-failed', { err });
        }
      } catch (err) {
        this.log.error('bc-create-failed', { err });
        this.ch = null;
      }
    } else {
      this.log.warn('bc-unsupported', { channel: this.CHANNEL });
      this.ch = null;
    }
  }

  _announce(type, payload = {}) {
    if (this.leaving && type !== 'bye') return;
    const msg = { type, ...payload, id: this.id, ts: Date.now() };
    // 1) BroadcastChannel
    if (this.ch) {
      try { this.ch.postMessage(msg); this.log.debug('bc-send', { type, ...this._dbg() }); }
      catch (err) { this.log.error('bc-send-error', { type, err }); }
    }
    // 2) localStorage fallback for control (avoid spamming 'frame')
    if (type !== 'frame') {
      try { localStorage.setItem('__tab_joust__', JSON.stringify(msg)); this.log.debug('ls-write', { type }); }
      catch (err) { this.log.warn('ls-write-failed', { err, type }); }
    }
  }

  /* ================= Events ================= */

  _onMsg(evt) {
    const msg = evt?.data || {};
    const { type, id } = msg || {};
    if (!type || !id) return;

    if (type === '__probe__') {
      this.log.debug('bc-probe', { from: id });
      return;
    }

    this.log.debug('bc-recv', { type, from: id });

    if (type === 'hello' || type === 'ping' || type === 'pong' || type === 'vis') {
      // A tab that said 'bye' only rejoins with a fresh hello (bfcache restore)
      if (this.gone.has(id)) {
        if (type !== 'hello') { this.log.debug('ignored-gone', { type, from: id }); return; }
        this.gone.delete(id);
      }
      this._remember(id, { visible: !!msg.visible, now: Date.now(), joinedAt: typeof msg.joinedAt === 'number' ? msg.joinedAt : undefined });
      if (typeof msg.joinedAt === 'number') this._touchOrder(id, msg.joinedAt);
      this.log.debug('presence-updated', { id, visible: !!msg.visible, members: this.members.size });
      // Answer a newcomer straight away so it knows the roster before the round starts
      if (type === 'hello') this._announce('ping', { id: this.id, visible: this.visible, joinedAt: this.joinedAt });
      // Message handlers keep running when Chrome throttles a long-hidden tab's
      // timers to once a minute, so if our own heartbeat has gone quiet, answer
      // the others' pings instead: that keeps us on the roster while asleep.
      if (type === 'ping' && Date.now() - (this._lastPingAt || 0) > this.HEARTBEAT_MS * 1.5) {
        this._lastPingAt = Date.now();
        this._announce('pong', { id: this.id, visible: this.visible, joinedAt: this.joinedAt });
      }
    } else if (type === 'bye') {
      this.gone.add(id);
      this.members.delete(id);
      this._removeFromOrder(id, /*clearIfEmpty=*/true);
      this.log.info('peer-bye', { id, members: this.members.size });
      // A player has gone: stop right here rather than on the next (possibly throttled) tick
      if (this.game && (this.game.roles || []).includes(id)) {
        if (this._leaderId() === this.id) this._announce('end', {});
        this.game = null;
        this._renderIdle();
        this.log.info('round-abandoned', { reason: 'bye' });
      }
    } else if (type === 'start') {
      if (!this._startGame(msg)) return;
      this.log.info('start-recv', { winner: msg.winner, t0: msg.t0 });
    } else if (type === 'countdown') {
      if (this.game && this.roleIndex != null && Date.now() < this.game.t0) this._renderCountdown(this.game.t0);
    } else if (type === 'frame' || type === 'frameIdx') {
      if (!this.game) return;
      this._paintIdx(msg.idx);
      this.log.debug('frame-recv', { idx: msg.idx });
    } else if (type === 'end') {
      if (!this.game) return;
      this._applyWinFaviconIfNeeded(this.game.endIdx);
      this.game = null;
      this._renderIdle();
      this.log.info('end-recv');
    }
  }

  /** Adopt a round. Returns false for a round that has already finished (stale storage). */
  _startGame({ t0, winner, roles }) {
    const frames = this._buildFramesForWinner(winner);
    const endIdx = frames.length - 1 + this.TAIL_HOLD_FRAMES;
    if (Date.now() > this._roundEndsAt({ t0, endIdx })) return false;

    this.game = { t0, winner, frames, endIdx, roles };
    this._takeRoles(roles);
    // A tab that has only just opened may not have heard from the other players
    // yet: trust the dealt roster so housekeeping doesn't drop the round
    if (Array.isArray(roles)) for (const id of roles) if (!this.members.has(id)) this._remember(id, { now: Date.now() });
    if (this.roleIndex != null) this._applyRoundStartFavicon();
    return true;
  }

  /** Paint frame `idx` in this tab's row, and hand out the flag once the result is in. */
  _paintIdx(idx) {
    if (!this.game || this.roleIndex == null) return;
    const rows = this._rowsAtIdx(idx);
    if (rows[this.roleIndex] == null) return;
    this._setTitle(rows[this.roleIndex]);
    this._applyWinFaviconIfNeeded(idx);
  }

  _onStorage = (e) => {
    if (e.key === this.ORDER_KEY) {
      this.order = this._loadOrder();
      this._resolveRole(true);
      this.log.debug('order-storage-reload', { order: this.order });
    } else if (e.key === '__tab_joust__') {
      try {
        const data = JSON.parse(e.newValue);
        this.log.debug('ls-recv', { type: data?.type });
        this._onMsg({ data });
      } catch {}
    }
  };

  _onPageshow() {
    // Re-announce in case bfcache restored this tab
    this.leaving = false;
    this._announce('hello', { id: this.id, visible: this.visible, joinedAt: this.joinedAt });
    this.log.info('pageshow-hello', this._dbg());
  }

  _onPageHide = () => {
    // Tell the others straight away, rather than waiting for the heartbeat to go stale
    this._announce('bye', { id: this.id });
    this.leaving = true;
    this._removeFromOrder(this.id, /*clearIfEmpty=*/true);
    this.log.info('pagehide-removed', this._dbg());
  };

  _onVisibility() {
    this.visible = !document.hidden;
    this._remember(this.id, { visible: this.visible });
    if (this.visible) {
      // Back in front. Our timers may have been throttled while hidden, so the
      // roster's timestamps can't be trusted yet: refresh them and ask everyone
      // to check in (they answer a hello at once), then take over driving.
      const now = Date.now();
      for (const m of this.members.values()) m.last = now;
      this._announce('hello', { id: this.id, visible: this.visible, joinedAt: this.joinedAt });
      if (this.game && now >= this.game.t0) {
        this._paintIdx(Math.min(Math.floor((now - this.game.t0) / this.FRAME_PERIOD_MS), this.game.endIdx));
      }
      this._tick();
    } else {
      this._announce('vis', { id: this.id, visible: this.visible });
      this._ensureLeaderLoops();
    }
    this.log.info('visibility', { visible: this.visible, ...this._dbg() });
  }

  /* ========== Presence / leader / roles ========== */

  _applyRoundStartFavicon() {
    this.hasAppliedWinIcon = false;
    try { this.favicon.setBlank(); } catch {}
  }

  _applyWinFaviconIfNeeded(currentIdx) {
    if (!this.game || this.hasAppliedWinIcon) return;
    if (currentIdx < this.game.endIdx) return;

    const winner = this.game.winner;
    // roleIndex: 0 = first tab (left), 1 = middle, 2 = third tab (right)
    try {
      if (winner === 'left' && this.roleIndex === 0) {
        this.favicon.setFlag('blue');
      } else if (winner === 'right' && this.roleIndex === 2) {
        this.favicon.setFlag('red');
      } else {
        // non-winning tabs stay transparent
        this.favicon.setBlank();
      }
      this.hasAppliedWinIcon = true;
      this.log.info('favicon-win-applied', { winner, roleIndex: this.roleIndex });
    } catch (e) {
      this.log.warn('favicon-apply-failed', { e });
    }
  }


  _remember(id, { visible, now, joinedAt }) {
    const ts = now ?? Date.now();
    const prev = this.members.get(id) || {};
    this.members.set(id, {
      last: ts,
      visible: visible ?? prev.visible ?? false,
      // Our own join sequence is always current (a provisional value is recorded
      // before the sequence is claimed, and must not stick)
      joinedAt: id === this.id ? this.joinedAt : (joinedAt ?? prev.joinedAt),
    });
    if (id !== this.id) this._remember(this.id, { visible: this.visible, joinedAt: this.joinedAt });
  }

  /**
   * Who plays left, middle and right this round: the tabs actually present,
   * in the order they were opened. Dealt once at round start and pinned for
   * the round — the persisted order list can go stale and reshuffle mid-joust.
   */
  _dealRoles() {
    return Array.from(this.members.entries())
      .sort((a, b) => ((a[1].joinedAt ?? Infinity) - (b[1].joinedAt ?? Infinity)) || (a[0] < b[0] ? -1 : 1))
      .slice(0, 3)
      .map(([id]) => id);
  }

  _takeRoles(roles) {
    if (!Array.isArray(roles)) return;
    const idx = roles.indexOf(this.id);
    this.roleIndex = idx >= 0 ? idx : null;
  }

  _pruneMembers() {
    const now = Date.now();
    let removed = 0;
    for (const [id, info] of this.members) {
      if (now - (info.last || 0) > this.STALE_MS) {
        this.members.delete(id);
        this._removeFromOrder(id, /*clearIfEmpty=*/true);
        removed++;
      }
    }
    if (removed) this.log.warn('members-pruned', { removed, size: this.members.size });
    this._remember(this.id, { visible: this.visible });
  }

  _sortedIds() { return Array.from(this.members.keys()).sort(); }
  _visibleIdsSorted() { return this._sortedIds().filter(id => this.members.get(id)?.visible); }

  // Only a visible tab drives: Chrome throttles a hidden tab's timers to once
  // a second (once a minute after five minutes), which can neither drive a
  // 320ms frame nor judge who is still here. With nothing visible there is no
  // driver and the joust simply waits; handover is safe because frames are
  // timed off the wall clock.
  _leaderId() {
    return this._visibleIdsSorted()[0] || null;
  }

  _roundEndsAt(g) { return g.t0 + (g.endIdx + 1) * this.FRAME_PERIOD_MS; }

  _loadOrder() {
    try { return JSON.parse(localStorage.getItem(this.ORDER_KEY) || '[]') || []; }
    catch { return []; }
  }
  _saveOrder() {
    try { localStorage.setItem(this.ORDER_KEY, JSON.stringify(this.order)); } catch (err) { this.log.warn('order-save-failed', { err }); }
  }
  _ensureInOrder() {
    if (!this.order.some(x => x.id === this.id)) {
      this.order.push({ id: this.id, joinedAt: this.joinedAt });
      this.order = dedupeBy(this.order, x => x.id).sort((a,b)=> (a.joinedAt||0)-(b.joinedAt||0));
      this._saveOrder();
      this.log.info('order-added-self', { order: this.order });
    }
    this._resolveRole(true);
  }
  _touchOrder(id, joinedAt) {
    const idx = this.order.findIndex(x => x.id === id);
    if (idx === -1) this.order.push({ id, joinedAt: joinedAt || Date.now() });
    this.order = dedupeBy(this.order, x => x.id).sort((a,b)=> (a.joinedAt||0)-(b.joinedAt||0));
    this._saveOrder();
    this._resolveRole(true);
    this.log.debug('order-touch', { id, joinedAt, order: this.order.map(o=>o.id) });
  }
  _removeFromOrder(id, clearIfEmpty=false) {
    const before = this.order.length;
    this.order = this.order.filter(x => x.id !== id);
    if (this.order.length !== before) this._saveOrder();
    if (clearIfEmpty && this.order.length === 0) {
      try { localStorage.removeItem(this.ORDER_KEY); } catch {}
      try { localStorage.removeItem('__joust_seq__'); } catch {}
      this.log.info('order-cleared-all');
    }
    this._resolveRole(true);
  }
  _resolveRole(provisional=false) {
    if (this.game) return; // roles are dealt at round start and pinned until it ends
    const idx = this.order.findIndex(x => x.id === this.id);
    this.roleIndex = idx >= 0 ? idx : null;
    if (!provisional) this.log.info('role-resolved', this._dbg());
  }

  /* ========== Housekeeping / rounds ========== */

  _tick() {
    const now = Date.now();
    const iLead = this._leaderId() === this.id;

    this._pruneMembers();

    // A round that should be over (we slept through its 'end'): tidy up
    if (this.game && now > this._roundEndsAt(this.game) + 1000) {
      this.game = null;
      this._renderIdle();
      this.log.info('round-expired');
    }

    const ids = this._sortedIds();
    const liveCount = ids.length;

    // A player left mid-round: end it now rather than playing to an empty castle.
    // (Spectator tabs beyond the three players can come and go freely.) Only the
    // visible driver rules on this: a hidden tab's timers are throttled, so its
    // view of who is still here can lag; it acts on explicit 'bye'/'end' instead.
    const playerLeft = iLead && this.game && (this.game.roles || []).some(rid => !this.members.has(rid));
    if (playerLeft) {
      this._announce('end', {});
      this.game = null;
      this._renderIdle();
      this.log.info('round-abandoned', { members: liveCount });
    }

    // Players that aren't driving have no paint loop: show the countdown from here
    if (this.game && this.roleIndex != null && !iLead && now < this.game.t0) {
      this._renderCountdown(this.game.t0);
    }

    if (!this.game) {
      if (liveCount >= 3 && iLead) {
        // Start a new round; winner randomized each round. t0 is wall-clock
        // so every tab (and any later driver) counts frames from the same moment.
        const winner = Math.random() < 0.5 ? 'left' : 'right';
        const roles = this._dealRoles();
        const t0 = Date.now() + this.START_DELAY_MS;
        this._startGame({ t0, winner, roles });
        this._announce('start', { t0, winner, roles });
        this.log.info('start-sent', { winner, t0, members: liveCount });
      } else {
        this._renderIdle();
      }
    }

    this._ensureLeaderLoops();
  }

  _renderCountdown(t0) {
    const msLeft = Math.max(0, t0 - Date.now());
    const secsLeft = Math.ceil(msLeft / 1000);
    const circled = { 3:'③', 2:'②', 1:'①' }[secsLeft] || '①';
    const badge = this._badge();
    document.title = `${this.titleIcon} ${badge}Starting in ${circled}`;
  }

  _renderIdle() {
    // Only when NO game is pending/active: hand the tab back to the site
    if (this.game) return;
    if (document.title !== this._originalTitle) document.title = this._originalTitle;
    this.favicon.restore();
    this.hasAppliedWinIcon = false;
  }

  _badge() {
    const idx = (this.roleIndex ?? 0) + 1;
    return (this.debugTabIndex ? `[${idx}] ` : '');
  }

  /* ========== Leader loops (pinned) ========== */

  _ensureLeaderLoops() {
    const iAmLeader = this._leaderId() === this.id;
    const ready = iAmLeader && this.game;

    // Drift-corrected driver (uses setTimeout to exact next boundary)
    if (ready && !this.sendTimer) { this.log.debug('drive-start'); this._drive(); }
    if (!ready && this.sendTimer) { this.log.debug('drive-stop'); clearTimeout(this.sendTimer); this.sendTimer = null; }

    // Leader paints regardless of visibility (no flicker on tab switch)
    if (ready && !this.paintRaf) { this.paintRaf = requestAnimationFrame(this._raf); this.log.debug('raf-start'); }
    if (!ready && this.paintRaf) { cancelAnimationFrame(this.paintRaf); this.paintRaf = 0; this.log.debug('raf-stop'); }
  }

  _drive() {
    if (!this.game) { this.sendTimer = null; return; }

    const now = Date.now();
    const { t0, endIdx } = this.game;

    if (now < t0) {
      // Tick the countdown out to the other tabs once a second (their own
      // timers may be throttled), then wake exactly at t0
      this._announce('countdown', { t0 });
      const secsLeft = Math.ceil((t0 - now) / 1000);
      const delay = Math.max(0, Math.min(t0 - now, t0 - (secsLeft - 1) * 1000 - now));
      this.sendTimer = setTimeout(this._drive, delay);
      this.log.debug('drive-wait-t0', { delay });
      return;
    }

    const elapsed = now - t0;
    const idx = Math.floor(elapsed / this.FRAME_PERIOD_MS);

    if (idx > endIdx) {
      // End of round; housekeeping will schedule the next
      this._applyWinFaviconIfNeeded(endIdx);
      this._announce('end', {});
      this.game = null;
      this.sendTimer = setTimeout(this._drive, 50);
      this.log.info('drive-end-round');
      return;
    }

    // Broadcast tiny frame index (BC + LS fallback)
    this._announce('frame', { idx });
    try {
      localStorage.setItem('__tab_joust__', JSON.stringify({ type: 'frameIdx', id: this.id, idx, ts: Date.now() }));
      this.log.debug('ls-frameIdx-write', { idx });
    } catch (err) {
      this.log.warn('ls-frameIdx-failed', { err });
    }

    // Schedule exactly to next boundary
    const nextTime = t0 + (idx + 1) * this.FRAME_PERIOD_MS;
    const delay = Math.max(0, nextTime - Date.now());
    this.sendTimer = setTimeout(this._drive, delay);
    this.log.debug('drive-next', { idx, delay });
  }

  _raf = () => {
    if (!this.game) { this.paintRaf = 0; return; }
    const { t0, endIdx } = this.game;
    const now = Date.now();

    if (now < t0) {
      if (this.roleIndex != null) this._renderCountdown(t0);
      this.paintRaf = requestAnimationFrame(this._raf);
      return;
    }

    this._paintIdx(Math.min(Math.floor((now - t0) / this.FRAME_PERIOD_MS), endIdx));
    this.paintRaf = requestAnimationFrame(this._raf);
  };

  /* ========== Titles & frames ========== */

  _setTitle(row) {
    const badge = this._badge();
    document.title = `${this.titleIcon} ${badge}${row}`;
  }

  _buildFramesForWinner(winner) {
    const branch = (winner === 'right') ? this.branchRightWins : this.branchLeftWins;
    return this.common.concat(branch);
  }

  _rowsAtIdx(i) {
    const g = this.game;
    if (!g) return [this.IDLE_TITLE, this.IDLE_TITLE, this.IDLE_TITLE];
    const idx = Math.min(i, g.frames.length - 1);
    return g.frames[idx];
  }

  /* ========== Catch-up for late joiners ========== */

  _ingestLastControlFromStorage() {
    try {
      const raw = localStorage.getItem('__tab_joust__');
      if (!raw) return;
      const msg = JSON.parse(raw);
      if (!msg || !msg.type) return;
      this.log.debug('ls-catchup', { type: msg.type });

      if (msg.type === 'start') {
        if (this._startGame(msg)) this.log.info('catchup-start', { winner: msg.winner, t0: msg.t0 });
      } else if (msg.type === 'frameIdx' && this.game) {
        this._paintIdx(msg.idx);
        this.log.debug('catchup-frameIdx', { idx: msg.idx });
      }
    } catch (err) {
      this.log.warn('ls-catchup-failed', { err });
    }
  }

  /* ========== Atomic ordering (Web Locks or LS fallback) ========== */

  async _claimSequence() {
    const key = '__joust_seq__';
    const lockName = 'joust-order-lock';

    const assignSeq = () => {
      const v = parseInt(localStorage.getItem(key) || '0', 10) || 0;
      const next = v + 1;
      localStorage.setItem(key, String(next));
      return next;
    };

    if (navigator?.locks?.request) {
      this.log.debug('seq-via-weblocks');
      return await navigator.locks.request(lockName, { mode: 'exclusive' }, async () => assignSeq());
    }

    // Fallback: LS lock with expiry
    this.log.debug('seq-via-ls-lock');
    const lkey = '__joust_lock__';
    const owner = this.id;
    const ttl = 1500; // 1.5s
    let seq = null;

    for (let i = 0; i < 25; i++) {
      try {
        const lockVal = localStorage.getItem(lkey);
        const obj = lockVal ? JSON.parse(lockVal) : null;
        const now = Date.now();
        if (!obj || (now - (obj.ts || 0)) > ttl) {
          localStorage.setItem(lkey, JSON.stringify({ owner, ts: Date.now() }));
          const check = JSON.parse(localStorage.getItem(lkey) || '{}');
          if (check.owner === owner) {
            seq = assignSeq();
            localStorage.removeItem(lkey);
            break;
          }
        }
      } catch {}
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 30));
    }
    if (seq == null) seq = assignSeq();
    this.log.info('seq-assigned', { seq });
    return seq;
  }

  /* ========== Utilities ========== */

  _badgeIndex() { return (this.roleIndex ?? -1) + 1; }

  _dbg() {
    return {
      id: this.id.slice(0, 8),
      role: this._badgeIndex(),
      members: this.members.size,
      visible: this.visible,
      leader: (this._leaderId()?.slice?.(0, 8)) || null,
      order: this.order.map(o => o.id.slice(0, 8)),
      hasGame: !!this.game,
    };
  }

  _diagPulse() {
    const pulse = () => {
      if (!this.running) return;
      const ids = this._sortedIds();
      this.log.info('pulse', {
        now: Date.now(),
        ids: ids.map(x => x.slice(0, 8)),
        order: this.order.map(o => o.id.slice(0, 8)),
        role: this._badgeIndex(),
        visible: this.visible,
        members: this.members.size,
        game: this.game ? { winner: this.game.winner, t0: this.game.t0 } : null,
      });
      setTimeout(pulse, 3000);
    };
    pulse();
  }
  
}

/* ===== Favicon helper ===== */
class FaviconHelper {
  constructor({ size = 64, log } = {}) {
    this.size = size;
    this.log = log || { debug(){}, info(){}, warn(){}, error(){} };
    this._last = '';
  }

  setBlank() {
    const url = this._pngTransparent();
    this._apply(url);
  }

  setFlag(color = 'red') {
    const url = this._pngPennant(color);
    this._apply(url);
  }

  /** Put the page's own icons back once the joust is over. */
  restore() {
    if (!this._originals) return;
    for (const l of Array.from(document.querySelectorAll('link[rel*="icon"]'))) l.remove();
    for (const l of this._originals) document.head.appendChild(l);
    this._originals = null;
    this._last = '';
    this.log.debug?.('favicon-restored');
  }

  _apply(href) {
    if (!href || href === this._last) return;
    this._last = href;

    // Set aside the page's own icons the first time we take over
    const links = Array.from(document.querySelectorAll('link[rel*="icon"]'));
    if (!this._originals) this._originals = links.map((l) => l.cloneNode(true));
    for (const l of links) l.parentNode.removeChild(l);

    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = href; // data URL
    document.head.appendChild(link);
    this.log.debug?.('favicon-set', { bytes: href.length });
  }

  _makeCanvas() {
    const c = document.createElement('canvas');
    c.width = c.height = this.size;
    return c;
  }

  _pngTransparent() {
    const c = this._makeCanvas();
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = 'rgba(0,0,0,0.01)';
    ctx.fillRect(0, 0, 1, 1);
    return c.toDataURL('image/png');
  }

  _pngPennant(color) {
    const c = this._makeCanvas();
    const ctx = c.getContext('2d');
    const S = this.size;

    // clear
    ctx.clearRect(0,0,S,S);

    // mast
    ctx.lineWidth = Math.max(2, Math.round(S*0.06));
    ctx.strokeStyle = 'rgba(0,0,0,0.75)';
    ctx.beginPath();
    ctx.moveTo(S*0.28, S*0.15);
    ctx.lineTo(S*0.28, S*0.85);
    ctx.stroke();

    // pennant triangle
    const fill = (color === 'blue') ? '#1677ff'
               : (color === 'red')  ? '#ff2b2b'
               : color;
    ctx.beginPath();
    ctx.moveTo(S*0.30, S*0.18);
    ctx.lineTo(S*0.90, S*0.30);
    ctx.lineTo(S*0.30, S*0.42);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();

    // subtle outline
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = Math.max(1, Math.round(S*0.03));
    ctx.stroke();

    return c.toDataURL('image/png');
  }
}


/* ===== helpers ===== */
function dedupeBy(arr, keyFn){
  const seen = new Set();
  return arr.filter(x => {
    const k = keyFn(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// Minimal, flexible logger
function makeLogger(ns, level=3, customLogger) {
  const L = customLogger || console;
  const tag = (kind) => `[${ns}] ${kind}`;
  const should = (need) => level >= need;
  return {
    error: (m, o={}) => should(1) && L.error(tag(m), o),
    warn:  (m, o={}) => should(2) && L.warn(tag(m), o),
    info:  (m, o={}) => should(3) && L.info(tag(m), o),
    debug: (m, o={}) => should(4) && L.debug(tag(m), o),
  };
}

/* =======================
   Frame tables are NOT included in this file.
   Ensure these are defined/imported:
   - COMMON_FRAMES()
   - BRANCH_RIGHT_WINS()
   - BRANCH_LEFT_WINS()
   ======================= */


/* =======================
   Frame tables (VERBATIM)
   ======================= */

function COMMON_FRAMES() {
  return [
    ["🏰→﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏←🏰"],
    ["🏰﹏→﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏←﹏🏰"],
    ["🏰﹏﹏→﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏←﹏﹏🏰"],
    ["🏰﹏﹏﹏→﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏←﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏→﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏←﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏→﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏←﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏→﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏←﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏→﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏←﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏→﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏←﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏→﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏←﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏→﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏←﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏→","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","←﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","→﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏←","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏→﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏←﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏→𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙←﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏→﹏﹏﹏﹏﹏←﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏→﹏﹏﹏←﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏→﹏←﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏⚔️﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏🛡️🛡️﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏🛡️🗡️﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏⚔️﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏🛡️🛡️﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏🗡️🛡️﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
  ];
}

// RIGHT wins (left loses)
function BRANCH_RIGHT_WINS() {
  return [
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏👑﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏👑﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏👑﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏👑﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰🔥👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🔥🔥👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰🔥👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🔥🔥👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰🔥👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🔥🔥👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["﹏﹏ 👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["﹏﹏ 👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["﹏﹏ 👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕽𝖎𝖌𝖍𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰→﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏←🏰"],
  ];
}

// LEFT wins (right loses)
function BRANCH_LEFT_WINS() {
  return [
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏👑﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏👑﹏﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏👑﹏﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏👑﹏﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏👑﹏﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑🔥🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑🔥🔥"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑🔥🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑🔥🔥"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑🔥🏰"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗 𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏"],
    ["🏰﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏𝕾𝖎𝖗﹏𝕷𝖊𝖋𝖙﹏𝖂𝖎𝖓𝖘﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏👑﹏﹏"],
    ["🏰→﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏","﹏﹏﹏𝖂𝖎𝖑𝖑﹏𝕶𝖓𝖎𝖌𝖍𝖙﹏﹏﹏","﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏﹏←🏰"],
  ];
}
